import { Fragment, useEffect, useState } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";

import { splitCamelCase, toTitleCase } from "~/utils/stringConverters";

export type FilterAutocompleteParams = {
  options: Array<any>;
  label: string;
  filters: { [key: string]: any };
  setFilters: (_filter: { key: any; value: any }) => void;
  filterKey: string;
  defaultValue?: any;
  getChipStyle?: (_option: any, _index: number) => object;
  valueSortKey?: string;
  isLoading?: boolean;
  noTooltip?: boolean;
  variant?: "outlined" | "standard" | "filled";
  [rest: string]: any;
};

const DEFAULT_VALUE: any[] = [];

const FilterAutocomplete = ({
  defaultValue = DEFAULT_VALUE,
  filterKey,
  filters,
  getChipStyle,
  groupKey: passedGroupKey,
  label,
  chipTooltipKey,
  multiple = true,
  noAutoselectAll = false,
  options: passedOptions,
  setFilters,
  titleSplit = false,
  valueSort,
  isLoading,
  noTooltip,
  variant,
  ...rest
}: FilterAutocompleteParams) => {
  const theme = useTheme();

  const [isFocused, setIsFocused] = useState(false);

  const groupKey = passedGroupKey ?? (multiple ? "group" : undefined);
  const needsGrouping = multiple && !passedGroupKey;
  const optionsArePrimitive = passedOptions.every((option) => typeof option === "string" || typeof option === "number");

  let options = needsGrouping
    ? passedOptions.map((option: any) => ({
        ...(typeof option === "string" || typeof option === "number" ? { value: option } : option),
        group: "default",
      }))
    : passedOptions;

  if (valueSort) {
    options = options.sort(valueSort);
  }

  useEffect(() => {
    if (multiple && !noAutoselectAll) {
      let value = options;
      if (needsGrouping && optionsArePrimitive) {
        value = value.map((option: any) => option.value);
      }

      setFilters({
        key: filterKey,
        value,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedOptions]);

  return (
    <Autocomplete
      getOptionLabel={(option) => option.value ?? option.label ?? option}
      multiple={multiple}
      onBlur={() => {
        setIsFocused(false);
      }}
      onChange={(_, newValue) => {
        const isArray = Array.isArray(newValue);
        const pendingValue: any = isArray ? [] : newValue;
        if (optionsArePrimitive && isArray) {
          newValue?.forEach((value: any) => {
            const theValue = value.group ? value.value : value;

            if (pendingValue.includes(theValue)) {
              pendingValue.splice(pendingValue.indexOf(theValue), 1);
            } else {
              pendingValue.push(theValue);
            }
          });
        }

        setFilters({
          key: filterKey,
          value: (optionsArePrimitive ? pendingValue : newValue) ?? defaultValue,
        });
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      options={options}
      renderInput={(params) => {
        let transform = false;
        if (!isFocused) {
          if (multiple) {
            transform =
              !Array.isArray(params.InputProps.startAdornment) ||
              (Array.isArray(params.InputProps.startAdornment) && params.InputProps.startAdornment.length === 0);
          } else {
            transform =
              !filters[filterKey] ||
              (typeof filters[filterKey] === "object" ? Object.keys(filters[filterKey]) : filters[filterKey]).length < 1;
          }
        }

        return (
          <Box
            sx={[
              transform && {
                "& .MuiInputLabel-root": {
                  transform: `translate(${theme.spacing(1.5)}, ${theme.spacing(0.6)})`,
                },
              },
            ]}
          >
            <Tooltip title={noTooltip ? "" : (params.inputProps.value ?? "")}>
              <TextField
                {...params}
                {...(!!variant && { variant })}
                label={label}
                required={rest.required}
                size="small"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            </Tooltip>
          </Box>
        );
      }}
      {...(groupKey && { groupBy: (option: any) => option[groupKey] })}
      {...(multiple && {
        value: filters[filterKey],
        renderOption: (props, option: any, { selected: selectedState }) => {
          const { key: autoKey, ...optionProps } = props;

          const key = option.id ?? autoKey;
          const selected = selectedState || (optionsArePrimitive && filters[filterKey].includes(option.value));

          let label = option.value ?? option.label ?? option;
          if (titleSplit) {
            label = toTitleCase(splitCamelCase(label));
          }

          return (
            <Box key={key} component="li" {...optionProps}>
              <Checkbox
                checked={selected}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                sx={{
                  p: 0.5,
                }}
              />
              {label}
            </Box>
          );
        },
        renderValue: (value, getItemProps: (_params: { index: number }) => { key: string }) =>
          !value.length
            ? ""
            : (valueSort ? value.sort(valueSort) : value).map((option: any, index: number) => {
                const { key, ...itemProps } = getItemProps({ index });

                let label = option.chipLabel ?? option.value ?? option.label ?? option;
                if (titleSplit) {
                  label = toTitleCase(splitCamelCase(label));
                }

                return (
                  <Tooltip key={key} title={option[chipTooltipKey] ?? ""}>
                    <Chip
                      {...itemProps}
                      label={label}
                      sx={{
                        "&.MuiAutocomplete-tag": {
                          height: "100%",
                          my: 0,
                        },
                        ...getChipStyle?.(option, index),
                      }}
                    />
                  </Tooltip>
                );
              }),
        ...(groupKey && {
          renderGroup: (params: { key: string; group: string; children: React.ReactNode }) => {
            const groupOptions = options.filter((option: any) => option[groupKey] === params.group);
            const groupedOptionsCount = groupOptions.length;

            const currentFilters = filters[filterKey] || [];
            const checkedInGroup = groupOptions.filter((opt: any) => {
              const val = optionsArePrimitive ? opt.value : opt;
              return optionsArePrimitive ? currentFilters.includes(val) : currentFilters.some((f: any) => f.id === opt.id);
            });

            const checkedCount = checkedInGroup.length;
            const isAllChecked = groupedOptionsCount === checkedCount;
            const isIndeterminate = checkedCount > 0 && !isAllChecked;

            const handleToggleGroup = (e: React.MouseEvent) => {
              e.preventDefault();

              let newValue;
              if (isAllChecked) {
                newValue = currentFilters.filter((f: any) => {
                  const idToMatch = optionsArePrimitive ? f : f.id;
                  return !groupOptions.some((opt: any) => (optionsArePrimitive ? opt.value : opt.id) === idToMatch);
                });
              } else {
                const otherGroupFilters = currentFilters.filter((f: any) => {
                  const idToMatch = optionsArePrimitive ? f : f.id;
                  return !groupOptions.some((opt: any) => (optionsArePrimitive ? opt.value : opt.id) === idToMatch);
                });
                const groupValues = groupOptions.map((opt: any) => (optionsArePrimitive ? opt.value : opt));
                newValue = [...otherGroupFilters, ...groupValues];
              }

              setFilters({
                key: filterKey,
                value: newValue.length === 0 ? defaultValue : newValue,
              });
            };

            return (
              <Fragment key={params.key}>
                <Stack
                  alignItems="center"
                  component="li"
                  direction="row"
                  onClick={handleToggleGroup}
                  pl={1}
                  py={0.5}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Checkbox
                    checked={isAllChecked}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    indeterminate={isIndeterminate}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                    disableRipple
                    readOnly
                  />
                  <Typography variant="body1">
                    {!needsGrouping ? toTitleCase(splitCamelCase(params.group)) : `${checkedCount === 0 ? "Select" : "Deselect"} All`} (
                    {checkedCount} / {groupedOptionsCount})
                  </Typography>
                </Stack>
                <Box pl={2}>{params.children}</Box>
              </Fragment>
            );
          },
        }),
      })}
      disableCloseOnSelect
      {...rest}
      sx={{
        width: "50%",
        "&.MuiAutocomplete-root .MuiInputBase-root": {
          pt: 0.5,
          pb: 0,
        },
        ...rest.sx,
      }}
    />
  );
};

export default FilterAutocomplete;
